import { MdShowChart } from 'react-icons/md';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AgentApiAnalysisSalesTrend } from '@/types/serializers';
import InsightEmptyState from '../InsightEmptyState';
import SalesRegisterButton from './SalesRegisterButton';
import {
  AXIS_LABEL_COLOR,
  AXIS_TICK_COLOR,
  CHART_FOCUS_RESET,
  GRID_COLOR,
  formatPeriodLabel,
  formatYen,
} from './salesChartUtils';

export type SalesTrendMode = 'forecast' | 'confirmed';

const AREA_COLOR = '#005FFF';

interface Props {
  trend: AgentApiAnalysisSalesTrend;
  mode: SalesTrendMode;
  onModeChange: (mode: SalesTrendMode) => void;
}

type TooltipPayloadItem = { value?: number | string; payload?: { period?: string } };
type TooltipProps = { active?: boolean; payload?: TooltipPayloadItem[] };

const MODE_OPTIONS: { value: SalesTrendMode; label: string }[] = [
  { value: 'forecast', label: '売上見込み' },
  { value: 'confirmed', label: '決定売上' },
];

function SalesTrendTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0];
  return (
    <div className="flex flex-col gap-xxs rounded-sm bg-white p-xs text-body-xs text-main shadow-layer2">
      {item.payload?.period && <span>{formatPeriodLabel(item.payload.period)}</span>}
      <span className="font-bold">{formatYen(Number(item.value) || 0)}</span>
    </div>
  );
}

/**
 * 売上推移のエリアチャート。売上見込み / 決定売上 をトグルで切り替えて表示する。
 */
export default function SalesTrendChart({ trend, mode, onModeChange }: Props) {
  const dataKey = mode === 'forecast' ? 'forecast_value' : 'confirmed_value';
  const hasData = trend.points.some((p) => p.forecast_value > 0 || p.confirmed_value > 0);

  const toggle = (
    <div className="flex h-[42px]">
      {MODE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onModeChange(opt.value)}
          className={`border border-solid border-border px-3 text-sm transition-colors first:rounded-l-sm last:-ml-px last:rounded-r-sm ${
            mode === opt.value
              ? 'bg-primary_lighten font-bold text-primary'
              : 'bg-white text-sub hover:bg-gray-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  if (!hasData) {
    return (
      <div className="w-full">
        <div className="mb-sm flex justify-end">
          <SalesRegisterButton />
        </div>
        <InsightEmptyState
          icon={MdShowChart}
          description="データを登録すると、ここにグラフが表示されます"
        />
      </div>
    );
  }

  return (
    <div className={`w-full ${CHART_FOCUS_RESET}`}>
      <div className="mb-sm">{toggle}</div>

      <ResponsiveContainer width="100%" height={340}>
        <AreaChart
          data={trend.points as Array<Record<string, unknown>>}
          margin={{ top: 24, right: 32, left: 8, bottom: 12 }}
        >
          <defs>
            <linearGradient id="salesTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={AREA_COLOR} stopOpacity={0.25} />
              <stop offset="100%" stopColor={AREA_COLOR} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="period"
            tickFormatter={formatPeriodLabel}
            tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickCount={6}
            allowDecimals={false}
            width={80}
            tickFormatter={(v: number) => formatYen(v)}
            tick={{ fontSize: 12, fill: AXIS_LABEL_COLOR }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<SalesTrendTooltip />} />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={AREA_COLOR}
            strokeWidth={2}
            fill="url(#salesTrendFill)"
            dot={{ r: 3, fill: AREA_COLOR }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
